// src/components/Admin/RutasDidacticasComp/RutasDidacticasForm/RutasDidacticasFormStep3Share.js

import React, { useMemo, useState } from "react";
import { Modal, Form, Radio, Button, Message } from "semantic-ui-react";
import { Radio as AntRadio, Button as AntBtn, Alert, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { DidacticRoutesAPI } from "../../../../api";
import { getShareScopeBadgeProps } from "../../../../Utils/shareScope";
import { NEW_SALAS } from "../../../../ui/featureFlags";
import "../../../../Utils/shareScope.scss";
import "./RutasStep.scss";

const api = new DidacticRoutesAPI();

export function RutasDidacticasFormStep3Share({
  accessToken,
  teacher,
  route, // { _id, nameRute, share?... }
  onClose,
  onReload,
}) {
  const { t } = useTranslation("itineraries");
  const hasInstitution = Boolean(teacher?.institution?._id);

  const initialScope = useMemo(
    () => route?.share?.scope ?? "none",
    [route?.share?.scope],
  );

  const [scope, setScope] = useState(initialScope);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const badge = useMemo(() => getShareScopeBadgeProps(scope), [scope]);

  const handleFinish = async () => {
    try {
      setError("");
      setSaving(true);

      if (!accessToken) {
        throw new Error(t("form.noAccessToken"));
      }
      if (!teacher?._id) {
        throw new Error(t("form.noTeacher"));
      }
      if (!route?._id) {
        throw new Error(t("form.noItinerary"));
      }

      await api.shareRoute(accessToken, teacher._id, route._id, {
        scope, // "none" | "institution" | "global"
        institutionId: hasInstitution ? teacher.institution._id : undefined,
      });

      onReload?.();
      onClose?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.message || t("form.shareUpdateError"));
    } finally {
      setSaving(false);
    }
  };

  if (NEW_SALAS) {
    return (
      <div className="rutas-step">
        <Typography.Title level={4} className="rutas-step__title">
          {t("form.shareHeader")}
        </Typography.Title>

        <p style={{ marginBottom: 12 }}>
          {t("form.shareHeader")}: <strong>{route?.nameRute || "—"}</strong>{" "}
          <span className={badge.className} title={badge.title}>
            {badge.text}
          </span>
        </p>

        {error ? (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <AntRadio.Group
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <AntRadio value="none">{t("form.sharePrivate")}</AntRadio>
          <AntRadio value="institution" disabled={!hasInstitution}>
            {t("form.shareInstitution")}
          </AntRadio>
          <AntRadio value="global">{t("form.shareGlobal")}</AntRadio>
        </AntRadio.Group>

        {!hasInstitution ? (
          <Alert
            type="info"
            showIcon
            message={t("form.noInstitutionInfo")}
            style={{ marginTop: 12 }}
          />
        ) : null}

        <div className="rutas-step__footer" style={{ marginTop: 18 }}>
          <AntBtn size="large" onClick={onClose} disabled={saving}>
            {t("form.closeButton")}
          </AntBtn>
          <AntBtn
            type="primary"
            size="large"
            loading={saving}
            onClick={handleFinish}
          >
            {t("form.saveAndFinish")}
          </AntBtn>
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal.Header>{t("form.shareHeader")}</Modal.Header>

      <Modal.Content>
        <p>
          {t("form.shareHeader")}: <strong>{route?.nameRute || "—"}</strong>{" "}
          <span className={badge.className} title={badge.title}>
            {badge.text}
          </span>
        </p>

        {error ? <Message negative content={error} /> : null}

        <Form>
          <Form.Field>
            <Radio
              label={t("form.sharePrivate")}
              name="scope"
              value="none"
              checked={scope === "none"}
              onChange={() => setScope("none")}
            />
          </Form.Field>

          <Form.Field>
            <Radio
              label={t("form.shareInstitution")}
              name="scope"
              value="institution"
              checked={scope === "institution"}
              onChange={() => setScope("institution")}
              disabled={!hasInstitution}
            />
          </Form.Field>

          <Form.Field>
            <Radio
              label={t("form.shareGlobal")}
              name="scope"
              value="global"
              checked={scope === "global"}
              onChange={() => setScope("global")}
            />
          </Form.Field>

          {!hasInstitution ? (
            <Message info size="tiny">
              {t("form.noInstitutionInfo")}
            </Message>
          ) : null}
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} disabled={saving}>
          {t("form.closeButton")}
        </Button>
        <Button
          primary
          onClick={handleFinish}
          loading={saving}
          disabled={saving}
        >
          {t("form.saveAndFinish")}
        </Button>
      </Modal.Actions>
    </>
  );
}
